"use client";
import { useEffect, useRef, useState, useCallback } from "react";
import Image from "next/image";

const STATS = [
  { target: 35, suffix: "%", label: "Recovery Rate" },
  { target: 3,  suffix: "x", label: "vs Manual Process" },
  { target: 24, suffix: "/7", label: "Monitoring" },
  { target: 0,  suffix: "",  label: "Manual Work" },
];

function StatCard({ target, suffix, label }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started || target === 0) return;
    const duration = 2000;
    let startTime = null;
    let frame;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [started, target]);

  return (
    <div ref={ref} className="p-6 rounded-2xl border bg-white text-center">
      <p className="text-3xl font-bold">
        {count}{suffix}
      </p>
      <p className="text-xs text-gray-600">{label}</p>
    </div>
  );
}

export default function Landing() {
  const [showHubSpotEmbed, setShowHubSpotEmbed] = useState(false)
  const HUBSPOT_MEETING_URL = process.env.NEXT_PUBLIC_HUBSPOTAPI_KEY

  const utmParams = {
    ref: 'revinu-hero',
    utm_source: 'website',
    utm_medium: 'cta',
    utm_campaign: 'revinu',
    utm_content: 'revinu__hero__request_demo',
  }

  const addUtmToUrl = (url) => {
    if (!url) return url
    const params = new URLSearchParams(utmParams)
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${params.toString()}`
  }

  const openHubSpotBooking = () => {
    setShowHubSpotEmbed(true)
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      Object.entries(utmParams).forEach(([key, value]) => {
        currentUrl.searchParams.set(key, value)
      })
      window.history.replaceState({}, '', currentUrl.pathname + '?' + currentUrl.searchParams.toString())
    }
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden'
    }
  }

  const closeHubSpotBooking = useCallback(() => {
    setShowHubSpotEmbed(false)
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      Object.keys(utmParams).forEach((key) => {
        currentUrl.searchParams.delete(key)
      })
      const newQuery = currentUrl.searchParams.toString()
      window.history.replaceState({}, '', currentUrl.pathname + (newQuery ? `?${newQuery}` : ''))
    }
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'unset'
    }
  }, [])

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showHubSpotEmbed) closeHubSpotBooking()
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleEscape)
      return () => window.removeEventListener('keydown', handleEscape)
    }
  }, [showHubSpotEmbed, closeHubSpotBooking])

  return (
    <div className="flex flex-col w-full">

      {/* HERO */}
      <section className="px-6 lg:px-20 py-32 bg-gradient-to-br from-blue-50 via-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto flex flex-col items-center text-center gap-8">

          {/* Badge */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-sky-200 bg-white/80">
            <span className="w-3 h-3 rounded-full bg-sky-500"></span>
            <p className="text-xs font-bold uppercase text-sky-700 tracking-widest">
              Intelligent Payment Recovery
            </p>
          </div>

          {/* Title */}
          <h1 className="text-5xl lg:text-7xl font-bold max-w-4xl">
            Stop Losing Revenue to <br />
            <span className="text-indigo-600">Failed Payments</span>
          </h1>

          {/* Description */}
          <p className="text-gray-600 text-lg max-w-3xl">
            Revinu detects failed recurring payments, categorizes root causes,
            triggers smart retries, and sends personalized outreach automatically.
          </p>

          <p className="text-sm text-gray-500">
            Built for SaaS, fintech, e-commerce, education & membership platforms.
          </p>

          {/* Buttons */}
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={openHubSpotBooking}
              className="px-8 py-4 bg-indigo-600 text-white rounded-lg font-semibold">
              Request a Demo →
            </button>

            <button
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 border border-gray-300 rounded-lg font-semibold">
              Explore Revinu
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 w-full max-w-4xl pt-10">
            {STATS.map((s, i) => (
              <StatCard key={i} target={s.target} suffix={s.suffix} label={s.label} />
            ))}
          </div>

        </div>
      </section>

      {/* TRUSTED BY */}
      <section className="border-y py-12 px-6 lg:px-20">
        <div className="max-w-[1750px] mx-auto text-center space-y-10">
          <p className="text-sm text-gray-500">
            Gateway-agnostic. Infrastructure-ready. Enterprise compatible.
          </p>

          <div className="flex flex-wrap justify-center gap-16 opacity-40 text-2xl font-bold">
            <span>Stripe</span>
            <span>Razorpay</span>
            <span>PayPal</span>
            <span>Adyen</span>
            <span>Braintree</span>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="px-6 lg:px-20 py-32">
        <div className="max-w-[1750px] mx-auto flex flex-col items-center gap-16">

          <div className="text-center max-w-2xl space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Services Designed to Protect Recurring Revenue
            </h2>
            <p className="text-gray-600">
              Comprehensive tools to automate recovery and retain customers.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 w-full">
            {[
              { title: "Real-Time Failure Detection", icon: "/revinu/a1.png" },
              { title: "Intelligent Categorization", icon: "/revinu/a2.png" },
              { title: "Smart Retry Engine", icon: "/revinu/a3.png" },
              { title: "Personalized Communication", icon: "/revinu/a4.png" },
              { title: "Fraud-Aware Logic", icon: "/revinu/a5.png" },
              { title: "Automated Reporting", icon: "/revinu/a6.png" },
            ].map(({ title, icon }, i) => (
              <div key={i} className="relative overflow-hidden p-8 rounded-2xl border bg-white space-y-4">
                {/* Decorative blur on cards 1, 3, 5 */}
                {i % 2 === 0 && (
                  <div
                    className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[#e0f2fe] shrink-0"
                    style={{ filter: "blur(20px)" }}
                  />
                )}
                <div className="relative w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Image src={icon} alt={title} width={32} height={32} className="object-contain" />
                </div>
                <h3 className="relative text-xl font-bold">{title}</h3>
                <p className="relative text-gray-600 text-sm">
                  Intelligent automation designed to recover revenue and reduce churn automatically.
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CTA */}
   

      {/* FOOTER */}

      {/* HubSpot Modal */}
      {showHubSpotEmbed && (
        <div
          id='hubspot-embed-section'
          className='fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center'
          onClick={closeHubSpotBooking}
        >
          <div
            className='relative w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:mx-4 sm:max-w-4xl sm:rounded-2xl'
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeHubSpotBooking}
              aria-label='Close booking calendar'
              className='absolute top-2 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md text-gray-700 transition-colors hover:bg-gray-100'
            >
              <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
            <div className='overflow-hidden' style={{ marginTop: '-48px' }}>
              <iframe
                src={addUtmToUrl(HUBSPOT_MEETING_URL)}
                title='Schedule a Call'
                className='w-full border-0'
                style={{ height: 'calc(85vh + 48px)', border: 'none' }}
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}